import { BehaviorSubject } from 'rxjs';
import { OrganizationSchema } from '../../definitions/schemas';
import { UserRolesStore } from './UserStore';
import { OrganizationStatus } from '../../enums/OrganizationStatus';

const NO_ORG_ID = '0';

class OrganizationsStore {
  private static instance: OrganizationsStore;

  private currentOrganizationSubject: BehaviorSubject<string>;

  private organizationsSubject: BehaviorSubject<OrganizationSchema[]>;

  constructor() {
    this.currentOrganizationSubject = new BehaviorSubject<string>(NO_ORG_ID);
    this.organizationsSubject = new BehaviorSubject<OrganizationSchema[]>([]);
  }

  public add(organization: OrganizationSchema) {
    if (!organization.id) throw new Error('Missing organization ID');
    if (!this.check(organization.id)) {
      const organizations = this.get();
      organizations.push(organization);
      this.set(organizations);
    }
  }

  public check(id: string) {
    return this.get().some(
      (organization: OrganizationSchema) => organization.id === id
    );
  }

  public clear() {
    this.organizationsSubject.next([]);
  }

  public clearCurrent() {
    this.currentOrganizationSubject.next(NO_ORG_ID);
  }

  public get() {
    return this.organizationsSubject.getValue();
  }

  public getById(id: string) {
    return this.organizationsSubject
      .getValue()
      .find((organization: OrganizationSchema) => organization.id === id);
  }

  public getCurrent() {
    const id = this.currentOrganizationSubject.getValue();
    // if (id === NO_ORG_ID) debugger; // @todo avoid unset org calls
    return id;
  }

  public update(newOrganization: OrganizationSchema) {
    const organizations = [...this.organizationsSubject.getValue()].map(
      (organization) => {
        if (organization.id === newOrganization.id) {
          return { ...organization, ...newOrganization };
        }
        return organization;
      }
    );
    this.set(organizations);
  }

  public getCurrentOrganization() {
    const id = this.getCurrent();
    let org: OrganizationSchema | undefined;
    if (id) {
      org = this.get().find((organization: OrganizationSchema) =>
        organization.id ? organization.id === id : false
      );
    }
    return org;
  }

  public getCurrentObservable() {
    return this.currentOrganizationSubject.asObservable();
  }

  public getCurrentRole() {
    const id = this.getCurrent();
    return UserRolesStore.getRoleByOrganizationId(id);
  }

  public getObservable() {
    return this.organizationsSubject.asObservable();
  }

  public hasMany() {
    return this.get().length > 1;
  }

  public isCurrentSet() {
    return this.getCurrent() !== NO_ORG_ID;
  }

  public set(organizations: OrganizationSchema[]) {
    this.organizationsSubject.next(organizations);
  }

  public isOrganizationCreating() {
    const org = this.getCurrentOrganization();
    if (!org) {
      return false;
    }
    return org.status === OrganizationStatus.Creating;
  }

  public setCurrent(id: string) {
    const check = this.check(id);
    if (check) this.currentOrganizationSubject.next(id);
    return check;
  }

  public static getInstance(): OrganizationsStore {
    if (!OrganizationsStore.instance) {
      OrganizationsStore.instance = new OrganizationsStore();
    }
    return OrganizationsStore.instance;
  }
}

// Export a singleton instance
const instance = OrganizationsStore.getInstance();
Object.freeze(instance);
export default instance;
